import React, { useContext, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import s from "./ReraitManager.module.css";
import { AuthContext } from "../../AuthContext";

const METRICS = [
  { value: "uniqViews", label: "Контакты" },
  { value: "uniqContacts", label: "Просмотры" },
  { value: "views_30_days", label: "Показы за 30 дней" },
];

const ORDERS = [
  { value: "random", label: "Случайный" },
  { value: "sequential", label: "По очереди" },
];

const defaultForm = (projectId = "") => ({
  id: undefined,
  enabled: true,
  randomizierbool: false,
  photounikalizatorbool: false,
  projectId,
  usedPage: "",
  periodDays: 30,
  metric: "uniqContacts",
  threshold: 100,
  dailyLimit: 20,
  selectionOrder: "random",
  runAt: "03:00",
});

function normalizeUsedPage(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (["none", "null"].includes(raw.toLowerCase())) return "";
  return raw;
}

function parseProjectSheets(usedPagesRaw) {
  if (!usedPagesRaw) return [];
  const list = String(usedPagesRaw)
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !["none", "null"].includes(part.toLowerCase()));
  return [...new Set(list)];
}

function metricLabel(value) {
  return METRICS.find((item) => item.value === value)?.label || value;
}

function projectNameById(projects, id) {
  const found = projects.find((p) => String(p.id) === String(id));
  return found ? `${found.name} (ID: ${found.id})` : `#${id}`;
}

function validateForm(form) {
  if (!form.projectId) return "Выберите проект";
  if (!form.periodDays || Number(form.periodDays) <= 0) return "Укажите корректный период";
  if (!form.metric) return "Выберите параметр для рерайта";
  if (form.threshold == null || Number(form.threshold) < 0) return "Укажите порог";
  if (!form.dailyLimit || Number(form.dailyLimit) <= 0) return "Укажите дневной лимит";
  if (!form.runAt) return "Укажите время запуска";
  return "";
}

function hasSheetConflict(jobs, form) {
  const targetProjectId = String(form.projectId);
  const targetSheet = normalizeUsedPage(form.usedPage);

  const sameProjectRows = jobs.filter(
    (row) =>
      String(row.project_id) === targetProjectId &&
      Number(row.id) !== Number(form.id)
  );

  if (targetSheet === "") {
    return sameProjectRows.length > 0;
  }

  return sameProjectRows.some((row) => {
    const rowSheet = normalizeUsedPage(row.used_page);
    return rowSheet === "" || rowSheet === targetSheet;
  });
}

function getProjectSheetBusyState(jobs, projectId, editingId) {
  const state = {
    hasAllSheetsRule: false,
    occupiedSheets: new Set(),
  };

  jobs.forEach((row) => {
    if (String(row.project_id) !== String(projectId)) return;
    if (editingId != null && Number(row.id) === Number(editingId)) return;

    const sheet = normalizeUsedPage(row.used_page);
    if (sheet === "") {
      state.hasAllSheetsRule = true;
      return;
    }

    state.occupiedSheets.add(sheet);
  });

  return state;
}

function mapJobToForm(job) {
  return {
    id: job.id,
    enabled: Boolean(job.enabled),
    randomizierbool: Boolean(job.randomizierbool),
    photounikalizatorbool: Boolean(job.photounikalizatorbool),
    projectId: String(job.project_id),
    usedPage: normalizeUsedPage(job.used_page),
    periodDays: Number(job.period_days),
    metric: String(job.metric),
    threshold: Number(job.threshold),
    dailyLimit: Number(job.daily_limit),
    selectionOrder: String(job.selection_order),
    runAt: job.run_at || "03:00",
  };
}

function mapFormToPayload(form) {
  return {
    project_id: Number(form.projectId),
    used_page: normalizeUsedPage(form.usedPage) || null,
    period_days: Number(form.periodDays),
    metric: form.metric,
    threshold: Number(form.threshold),
    daily_limit: Number(form.dailyLimit),
    selection_order: form.selectionOrder,
    run_at: form.runAt,
    enabled: Boolean(form.enabled),
    randomizierbool: Boolean(form.randomizierbool),
    photounikalizatorbool: Boolean(form.photounikalizatorbool),
  };
}

export default function ReraitManager({
  projects,
  fixedProjectId,
  targetUserId,
  title = "Авто переопубликация",
}) {
  const navigate = useNavigate();
  const { currentUser, role, loading: authLoading } = useContext(AuthContext);

  const normalizedProjects = useMemo(
    () =>
      (projects || []).map((p) => ({
        id: String(p.id),
        name: p.project_name || p.name || `Проект ${p.id}`,
        used_pages: p.used_pages || "",
      })),
    [projects]
  );

  const isFixedProject = fixedProjectId != null;
  const fixedId = isFixedProject ? String(fixedProjectId) : "";
  const hasProjects = normalizedProjects.length > 0;

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatingStatusIds, setUpdatingStatusIds] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [form, setForm] = useState(defaultForm(fixedId));

  const isAdminUser = role === "admin" || role === "superadmin";
  const hasAutoReraitAccess = useMemo(() => {
    if (isAdminUser) {
      return true;
    }

    const inTarif = currentUser?.services_in_tarif || [];
    const userServices = currentUser?.user_services || [];
    return [...inTarif, ...userServices].some(
      (item) => item?.technical_name === "auto_rerait" && Number(item?.value ?? 1) !== 0
    );
  }, [currentUser, isAdminUser]);

  async function apiList() {
    setLoading(true);
    setError("");
    try {
      const requestBody = targetUserId ? { user_id: Number(targetUserId) } : {};
      const res = await fetch("/api/rerait/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      if (!res.ok) throw new Error(`Ошибка загрузки: ${res.status}`);
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Не удалось загрузить правила");
    } finally {
      setLoading(false);
    }
  }

  async function apiCreate(payload) {
    const res = await fetch("/api/rerait/rerait", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, ...(targetUserId ? { user_id: Number(targetUserId) } : {}) }),
    });

    if (!res.ok) throw new Error(`Ошибка создания: ${res.status}`);
    return res.json();
  }

  async function apiUpdate(payload) {
    const res = await fetch("/api/rerait/rerait", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, ...(targetUserId ? { user_id: Number(targetUserId) } : {}) }),
    });

    if (!res.ok) throw new Error(`Ошибка обновления: ${res.status}`);
    return res.json();
  }

  async function apiDelete(id) {
    const res = await fetch("/api/rerait/rerait", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, ...(targetUserId ? { user_id: Number(targetUserId) } : {}) }),
    });

    if (!res.ok) throw new Error(`Ошибка удаления: ${res.status}`);
    return true;
  }

  useEffect(() => {
    if (authLoading) return;
    if (!hasAutoReraitAccess) return;
    if (!hasProjects) return;
    apiList();
  }, [authLoading, hasAutoReraitAccess, hasProjects]);

  const visibleJobs = useMemo(() => {
    if (!isFixedProject) return jobs;
    const fixedMatches = jobs.filter((j) => {
      const jobProjectId = j?.project_id ?? j?.projectId;
      return (
        String(jobProjectId) === fixedId ||
        Number(jobProjectId) === Number(fixedId)
      );
    });

    return fixedMatches.length > 0 ? fixedMatches : jobs;
  }, [jobs, fixedId, isFixedProject]);

  const selectedProjectId = String(form.projectId || fixedId || "");
  const selectedProject = normalizedProjects.find(
    (p) => String(p.id) === selectedProjectId
  );

  const busyState = useMemo(
    () => getProjectSheetBusyState(jobs, selectedProjectId, form.id),
    [jobs, selectedProjectId, form.id]
  );

  const sheetOptions = useMemo(() => {
    const fromProject = parseProjectSheets(selectedProject?.used_pages || "");
    if (form.usedPage && !fromProject.includes(form.usedPage)) {
      return [form.usedPage, ...fromProject];
    }
    return fromProject;
  }, [selectedProject?.used_pages, form.usedPage]);

  const openCreate = () => {
    if (!hasAutoReraitAccess) {
      setShowAccessModal(true);
      return;
    }

    const fallbackProject = isFixedProject
      ? fixedId
      : form.projectId || normalizedProjects[0]?.id || "";

    setForm(defaultForm(fallbackProject));
    setError("");
    setFormOpen(true);
  };

  const openEdit = (job) => {
    if (!hasAutoReraitAccess) {
      setShowAccessModal(true);
      return;
    }

    setForm(mapJobToForm(job));
    setError("");
    setFormOpen(true);
  };

  const handleStatusToggle = async (job) => {
    if (!hasAutoReraitAccess) {
      setShowAccessModal(true);
      return;
    }

    const jobId = String(job.id);
    const nextEnabled = !Boolean(job.enabled);
    const previousEnabled = Boolean(job.enabled);

    setUpdatingStatusIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
    setError("");
    setJobs((prev) =>
      prev.map((item) =>
        String(item.id) === jobId ? { ...item, enabled: nextEnabled } : item
      )
    );

    try {
      const payload = mapFormToPayload(mapJobToForm(job));
      const updated = await apiUpdate({ id: job.id, ...payload, enabled: nextEnabled });
      setJobs((prev) =>
        prev.map((item) => (String(item.id) === String(updated.id) ? updated : item))
      );
    } catch (e) {
      setJobs((prev) =>
        prev.map((item) =>
          String(item.id) === jobId ? { ...item, enabled: previousEnabled } : item
        )
      );
      setError(e.message || "Не удалось обновить статус");
    } finally {
      setUpdatingStatusIds((prev) => prev.filter((id) => id !== jobId));
    }
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(defaultForm(fixedId));
  };

  const handleRemove = async (job) => {
    if (!hasAutoReraitAccess) {
      setShowAccessModal(true);
      return;
    }

    if (!window.confirm("Удалить правило переопубликации?")) return;

    try {
      setLoading(true);
      setError("");
      await apiDelete(job.id);
      setJobs((prev) => prev.filter((item) => item.id !== job.id));
    } catch (e) {
      setError(e.message || "Не удалось удалить правило");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formError = validateForm(form);
    if (formError) {
      setError(formError);
      return;
    }

    if (hasSheetConflict(jobs, form)) {
      const isAllSheets = normalizeUsedPage(form.usedPage) === "";
      setError(
        isAllSheets
          ? "Нельзя создать правило для всех листов: у проекта уже есть правила по отдельным листам."
          : "Для этого листа правило уже существует, либо есть правило " +
              '"на все листы".'
      );
      return;
    }

    const payload = mapFormToPayload(form);

    try {
      setLoading(true);
      setError("");
      if (form.id != null) {
        const updated = await apiUpdate({ id: form.id, ...payload });
        setJobs((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
      } else {
        const created = await apiCreate(payload);
        setJobs((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (e) {
      setError(e.message || "Не удалось сохранить правило");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={s.root}>
      <div className={s.titleRow}>
        <h3 className={s.title}>{title}</h3>
        <div className={s.actions}>
          <button type="button" className={s.btn} onClick={apiList} disabled={loading}>
            Обновить
          </button>
          <button
            type="button"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={openCreate}
            disabled={
              authLoading || loading || (!isFixedProject && normalizedProjects.length === 0)
            }
          >
            Добавить правило
          </button>
        </div>
      </div>

      <div className={s.note}>
        Ограничение: на один лист можно только одно правило. Если есть правило на все листы,
        отдельные правила для листов создать нельзя.
      </div>

      {!authLoading && !hasAutoReraitAccess ? (
        <div className={s.accessWarning}>
          В вашем тарифе нет услуги авто переопубликации или она не подключена.
        </div>
      ) : null}

      {error ? <div className={s.error}>{error}</div> : null}

      {!authLoading && hasAutoReraitAccess && !hasProjects ? (
        <div className={s.empty}>
          <div>У вас пока нет проектов. Создайте проект, чтобы настроить авто переопубликацию.</div>
          <div className={s.formActions}>
            <button
              type="button"
              className={`${s.btn} ${s.btnPrimary}`}
              onClick={() => navigate("/dashboard?tab=create-project")}
            >
              Перейти к созданию проекта
            </button>
          </div>
        </div>
      ) : null}

      {hasProjects && visibleJobs.length ? (
        <>
        <div className={`${s.tableWrap} ${s.desktopTable}`}>
          <table className={s.table}>
            <thead>
              <tr>
                {!isFixedProject && <th>Проект</th>}
                <th>Лист</th>
                <th>Метрика</th>
                <th>Порог</th>
                <th>Лимит/день</th>
                {/* <th>Запуск</th> */}
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleJobs.map((job) => (
                <tr key={job.id}>
                  {!isFixedProject && (
                    <td>{projectNameById(normalizedProjects, job.project_id)}</td>
                  )}
                  <td>{normalizeUsedPage(job.used_page) || "Все"}</td>
                  <td>{metricLabel(job.metric)}</td>
                  <td>{job.threshold}</td>
                  <td>{job.daily_limit}</td>
                  {/* <td>{job.run_at}</td> */}
                  <td>
                    <div className={s.statusToggle}>
                      <label className={`${s.switch} ${updatingStatusIds.includes(String(job.id)) ? s.switchSaving : ""}`}>
                        <input
                          id={`rerait-enabled-${job.id}`}
                          type="checkbox"
                          checked={Boolean(job.enabled)}
                          onChange={() => handleStatusToggle(job)}
                          disabled={loading || updatingStatusIds.includes(String(job.id))}
                        />
                        <span className={s.slider}></span>
                      </label>
                      <div className={s.statusLabel}>{job.enabled ? "Активно" : "Пауза"}</div>
                    </div>
                  </td>
                  <td>
                    <div className={s.actions}>
                      <button type="button" className={s.btn} onClick={() => openEdit(job)}>
                        Редактировать
                      </button>
                      <button
                        type="button"
                        className={`${s.btn} ${s.btnDanger}`}
                        onClick={() => handleRemove(job)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={s.mobileCards}>
          {visibleJobs.map((job) => (
            <article key={`mobile-${job.id}`} className={s.jobCard}>
              {!isFixedProject ? (
                <div className={s.jobRow}>
                  <span className={s.jobLabel}>Проект</span>
                  <span className={s.jobValue}>
                    {projectNameById(normalizedProjects, job.project_id)}
                  </span>
                </div>
              ) : null}

              <div className={s.jobRow}>
                <span className={s.jobLabel}>Лист</span>
                <span className={s.jobValue}>{normalizeUsedPage(job.used_page) || "Все"}</span>
              </div>

              <div className={s.jobRow}>
                <span className={s.jobLabel}>Метрика</span>
                <span className={s.jobValue}>{metricLabel(job.metric)}</span>
              </div>

              <div className={s.jobRow}>
                <span className={s.jobLabel}>Порог</span>
                <span className={s.jobValue}>{job.threshold}</span>
              </div>

              <div className={s.jobRow}>
                <span className={s.jobLabel}>Лимит/день</span>
                <span className={s.jobValue}>{job.daily_limit}</span>
              </div>

              <div className={s.jobStatusRow}>
                <span className={s.jobLabel}>Статус</span>
                <div className={s.statusToggle}>
                  <label className={`${s.switch} ${updatingStatusIds.includes(String(job.id)) ? s.switchSaving : ""}`}>
                    <input
                      id={`rerait-enabled-mobile-${job.id}`}
                      type="checkbox"
                      checked={Boolean(job.enabled)}
                      onChange={() => handleStatusToggle(job)}
                      disabled={loading || updatingStatusIds.includes(String(job.id))}
                    />
                    <span className={s.slider}></span>
                  </label>
                  <div className={s.statusLabel}>{job.enabled ? "Активно" : "Пауза"}</div>
                </div>
              </div>

              <div className={s.actions}>
                <button type="button" className={s.btn} onClick={() => openEdit(job)}>
                  Редактировать
                </button>
                <button
                  type="button"
                  className={`${s.btn} ${s.btnDanger}`}
                  onClick={() => handleRemove(job)}
                >
                  Удалить
                </button>
              </div>
            </article>
          ))}
        </div>
        </>
      ) : hasProjects ? (
        <div className={s.empty}>Пока нет правил переопубликации.</div>
      ) : null}

      {formOpen && (
        <form className={s.formCard} onSubmit={handleSubmit}>
          <div className={s.grid}>
            <div className={s.field}>
              <label>Проект</label>
              <select
                value={form.projectId}
                onChange={(e) => setForm((prev) => ({ ...prev, projectId: e.target.value, usedPage: "" }))}
                disabled={isFixedProject}
                required
              >
                {!isFixedProject && <option value="">- выбрать проект -</option>}
                {normalizedProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} (ID: {project.id})
                  </option>
                ))}
              </select>
            </div>

            <div className={s.field}>
              <label>Лист таблицы</label>
              <select
                value={form.usedPage}
                onChange={(e) => setForm((prev) => ({ ...prev, usedPage: e.target.value }))}
                disabled={!form.projectId}
              >
                <option value="" disabled={busyState.occupiedSheets.size > 0}>
                  Все
                  {busyState.occupiedSheets.size > 0
                    ? " (недоступно: есть правила по листам)"
                    : ""}
                </option>
                {sheetOptions.map((sheetName) => (
                  <option
                    key={sheetName}
                    value={sheetName}
                    disabled={busyState.hasAllSheetsRule || busyState.occupiedSheets.has(sheetName)}
                  >
                    {sheetName}
                    {busyState.hasAllSheetsRule
                      ? " (недоступно: есть правило на все листы)"
                      : busyState.occupiedSheets.has(sheetName)
                        ? " (занято)"
                        : ""}
                  </option>
                ))}
              </select>
            </div>
{/* 
            <div className={s.field}>
              <label>Период (дней)</label>
              <input
                type="number"
                min={1}
                value={form.periodDays}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, periodDays: Number(e.target.value) }))
                }
                required
              />
            </div> */}

            <div className={s.field}>
              <label>Параметр для рерайта</label>
              <select
                value={form.metric}
                onChange={(e) => setForm((prev) => ({ ...prev, metric: e.target.value }))}
                required
              >
                {METRICS.map((metric) => (
                  <option key={metric.value} value={metric.value}>
                    {metric.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={s.field}>
              <label>Порог выбранной метрики</label>
              <input
                type="number"
                min={0}
                value={form.threshold}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, threshold: Number(e.target.value) }))
                }
                required
              />
            </div>

            <div className={s.field}>
              <label>Лимит обновлений в день</label>
              <input
                type="number"
                min={1}
                value={form.dailyLimit}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, dailyLimit: Number(e.target.value) }))
                }
                required
              />
            </div>

            <div className={s.field}>
              <label>Порядок выбора</label>
              <select
                value={form.selectionOrder}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, selectionOrder: e.target.value }))
                }
                required
              >
                {ORDERS.map((order) => (
                  <option key={order.value} value={order.value}>
                    {order.label}
                  </option>
                ))}
              </select>
            </div>

            {/* <div className={s.field}>
              <label>Время ежедневного запуска (МСК)</label>
              <input
                type="time"
                value={form.runAt}
                onChange={(e) => setForm((prev) => ({ ...prev, runAt: e.target.value }))}
                required
              />
            </div> */}
          </div>

          <div className={s.checkRow}>
            <input
              id="rerait-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            <label htmlFor="rerait-enabled">Задание активно</label>
          </div>

          <div className={s.checkRow}>
            <input
              id="rerait-photounikalizatorbool"
              type="checkbox"
              checked={form.photounikalizatorbool}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, photounikalizatorbool: e.target.checked }))
              }
            />
            <label htmlFor="rerait-photounikalizatorbool">уникализировать фото</label>
          </div>

          <div className={s.checkRow}>
            <input
              id="rerait-randomizierbool"
              type="checkbox"
              checked={form.randomizierbool}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, randomizierbool: e.target.checked }))
              }
            />
            <label htmlFor="rerait-randomizierbool">Заполнены колонки рандомайзера</label>
          </div>

          <div className={s.formActions}>
            <button type="submit" className={`${s.btn} ${s.btnPrimary}`} disabled={loading}>
              Сохранить
            </button>
            <button type="button" className={s.btn} onClick={closeForm}>
              Отмена
            </button>
          </div>
        </form>
      )}

      {showAccessModal ? (
        <div className={s.modalOverlay}>
          <div className={s.modalCard}>
            <h4 className={s.modalTitle}>Авто переопубликация недоступна</h4>
            <p className={s.modalText}>
              В вашем тарифе нет услуги авто переопубликации или дополнительная услуга еще не
              подключена.
            </p>
            <div className={s.formActions}>
              <button
                type="button"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={() => {
                  setShowAccessModal(false);
                  navigate("/dashboard?tab=payments");
                }}
              >
                Подключить
              </button>
              <button
                type="button"
                className={s.btn}
                onClick={() => setShowAccessModal(false)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
